import { REGIONAL_VEGETATION_TYPES } from '@ruminati/constants/gaf/shared'
import { type UserInputsT } from '@ruminati/types_v2/emissions-reports/forms'
import { type DairyCattleSnapshotEntryT } from '@ruminati/types_v2/emissions-reports/forms/dairy'
import { type DairyProductionSystemT as PropertyDairyProductionSystemT } from '@ruminati/types_v2/property'
import { type ReportInputsPartialT } from '@ruminati/types_v2/report'
import {
  type DairyCattleT,
  type DairyManureManagementT,
  type DairyProductionT,
  type DairyProductionSystemRecordT,
} from '@ruminati/types_v2/report_dairy'
import { type SupplementaryFeedT, type TreesT } from '@ruminati/types_v2/report_shared'
import { type SeasonsT } from '@ruminati/types_v2/utils/index'
import { type WorkBook, type WorkSheet, type CellObject } from 'xlsx'

import { parseFloatWithFixed } from './numbers'

const SUPPORTED_ADCC_MAJOR_VERSIONS = ['5', '6']
const ADCC_CELL_REFERENCE_REGEX = /^'?(.*?)'?!\$?([A-Z]+)\$?(\d+)$/
const UREA_N_FRACTION = 0.46

function getCellText(cell: CellObject | undefined): string {
  if (!cell) return ''
  if (typeof cell.w === 'string') return cell.w.trim()
  if (typeof cell.v === 'string') return cell.v.trim()
  if (typeof cell.v === 'number') return String(cell.v)
  return ''
}

function getCellNumber(cell: CellObject | undefined): number | null {
  if (!cell) return null
  if (typeof cell.v === 'number') return cell.v
  if (typeof cell.v === 'string' && cell.v !== '') {
    const numericValue = Number(cell.v)
    return Number.isFinite(numericValue) ? numericValue : null
  }
  return null
}

function getIntroductionTab(workbook: WorkBook): WorkSheet | undefined {
  return workbook.Sheets['Introduction']
}

function getMainFarmSheetName(workbook: WorkBook): string | null {
  if (workbook.Sheets['Your Farm']) return 'Your Farm'
  if (workbook.Sheets['Baseline farm']) return 'Baseline farm'
  return null
}

function getAdccVersionNumber(workbook: WorkBook): string | null {
  const introductionTab = getIntroductionTab(workbook)
  if (!introductionTab) return null

  // v6: "Version 6.1" in A3
  const v6Match = getCellText(introductionTab.A3).match(/Version\s+([0-9]+(?:\.[0-9]+)?)/i)
  if (v6Match) return v6Match[1]

  // v5: "Version 5.2, April 2024" in G7
  const v5Match = getCellText(introductionTab.G7).match(/Version\s+([0-9]+(?:\.[0-9]+)?)/i)
  if (v5Match) return v5Match[1]

  return null
}

function hasExpectedWorkbookStructure(workbook: WorkBook): boolean {
  return Boolean(getIntroductionTab(workbook) && getMainFarmSheetName(workbook))
}

interface NamedRangeRecord {
  name: string
  ref: string
}

export interface AdccWorkbookContext {
  versionNumber: string
  mainSheetName: string
  namedRanges: NamedRangeRecord[]
  getNamedTextValue: (namedRange: string) => string | null
  getNamedNumericValue: (namedRange: string) => number | null
  getNamedRangeRef: (namedRange: string) => string | null
  getSheetCellNumericValue: (sheetName: string, cellRef: string) => number | null
  getSheetCellText: (sheetName: string, cellRef: string) => string
}

interface AdccDairyClassMapping {
  classId: 1 | 2 | 3 | 4 | 5 | 6
  headcountRange: string
  liveweightRange: string
  liveweightGainRange?: string
  dmdRange: string
  crudeProteinRange: string
}

interface AdccDairySalesMapping {
  classId: 1 | 2 | 3 | 4 | 5 | 6 | 7
  numberSoldRange: string
  avgLiveweightRange: string
}

const ADCC_DAIRY_CLASS_MAPPINGS: AdccDairyClassMapping[] = [
  {
    classId: 1,
    headcountRange: 'CowMilkerNumber',
    liveweightRange: 'CowLiveweightKg',
    dmdRange: 'FeedGrazedTotalDigestabilityPercent',
    crudeProteinRange: 'FeedGrazedTotalProteinPercent',
  },
  {
    classId: 2,
    headcountRange: 'Rising2HeiferNumber',
    liveweightRange: 'Rising2HeiferLiveweightKg',
    liveweightGainRange: 'Rising2HeiferLwtGainKgPerDay',
    dmdRange: 'FeedFedOtherStockDigestibilityPercent',
    crudeProteinRange: 'FeedFedOtherStockProteinPercent',
  },
  {
    classId: 3,
    headcountRange: 'Rising1HeiferNumber',
    liveweightRange: 'Rising1HeiferLiveweightKg',
    liveweightGainRange: 'Rising1HeiferLwtGainKgPerDay',
    dmdRange: 'FeedFedOtherStockDigestibilityPercent',
    crudeProteinRange: 'FeedFedOtherStockProteinPercent',
  },
  {
    classId: 4,
    headcountRange: 'BullNumber',
    liveweightRange: 'BullLiveweightKg',
    dmdRange: 'FeedFedOtherStockDigestibilityPercent',
    crudeProteinRange: 'FeedFedOtherStockProteinPercent',
  },
  {
    classId: 5,
    headcountRange: 'OtherLivestockNumber',
    liveweightRange: 'OtherLivestockLiveweightKg',
    liveweightGainRange: 'OtherLivestockLwtGainKgPerDay',
    dmdRange: 'FeedFedOtherStockDigestibilityPercent',
    crudeProteinRange: 'FeedFedOtherStockProteinPercent',
  },
  {
    classId: 6,
    headcountRange: 'OtherLivestockNotImportedNumber',
    liveweightRange: 'OtherLivestockNotImportedLiveweightKg',
    liveweightGainRange: 'OtherLivestockNotImportedLwtGainKgPerDay',
    dmdRange: 'FeedFedOtherStockDigestibilityPercent',
    crudeProteinRange: 'FeedFedOtherStockProteinPercent',
  },
]

const ADCC_DAIRY_SALES_MAPPINGS: AdccDairySalesMapping[] = [
  {
    classId: 1,
    numberSoldRange: 'CowSalesNumber',
    avgLiveweightRange: 'CowSalesLiveweight',
  },
  {
    classId: 2,
    numberSoldRange: 'Rising2HeiferSalesNumber',
    avgLiveweightRange: 'Rising2HeiferSalesLiveweight',
  },
  {
    classId: 3,
    numberSoldRange: 'Rising1HeiferSalesNumber',
    avgLiveweightRange: 'Rising1HeiferSalesLiveweight',
  },
  {
    classId: 4,
    numberSoldRange: 'BullSalesNumber',
    avgLiveweightRange: 'BullSalesLiveweight',
  },
  {
    classId: 5,
    numberSoldRange: 'OtherLivestockSalesNumber',
    avgLiveweightRange: 'OtherLivestockSalesLiveweight',
  },
  {
    classId: 6,
    numberSoldRange: 'OtherLivestockNotImportedSalesNumber',
    avgLiveweightRange: 'OtherLivestockNotImportedSalesLiveweight',
  },
  {
    classId: 7,
    numberSoldRange: 'Dbcc_CalfSalesNumber',
    avgLiveweightRange: 'CalfSalesLiveweight',
  },
]

function getNamedRanges(workbook: WorkBook): NamedRangeRecord[] {
  const names = workbook.Workbook?.Names
  if (!Array.isArray(names)) return []

  return names
    .filter((entry): entry is { Name: string; Ref: string } => {
      return typeof entry?.Name === 'string' && typeof entry?.Ref === 'string'
    })
    .map((entry) => ({
      name: entry.Name,
      ref: entry.Ref,
    }))
}

function getCellByReference(workbook: WorkBook, ref: string): CellObject | null {
  const match = ref.match(ADCC_CELL_REFERENCE_REGEX)
  if (!match) return null

  const sheetName = match[1]
  const col = match[2]
  const row = match[3]
  const tab = workbook.Sheets[sheetName]
  if (!tab) return null

  const cellRef = `${col}${row}`
  return tab[cellRef] ?? null
}

export function buildAdccWorkbookContext(workbook: WorkBook): AdccWorkbookContext {
  if (!hasExpectedWorkbookStructure(workbook)) {
    throw Error('Does not appear to be an ADCC spreadsheet we support.')
  }

  const versionNumber = getAdccVersionNumber(workbook)
  const majorVersion = versionNumber?.split('.')[0]
  if (!versionNumber || !majorVersion || !SUPPORTED_ADCC_MAJOR_VERSIONS.includes(majorVersion)) {
    throw Error(`ADCC version ${versionNumber ?? 'unknown'} is not currently supported.`)
  }

  // Non-null asserted: hasExpectedWorkbookStructure already confirmed this exists
  const mainSheetName = getMainFarmSheetName(workbook)!

  const namedRanges = getNamedRanges(workbook)
  const namedRangeLookup = new Map(namedRanges.map((entry) => [entry.name, entry.ref]))

  function getNamedRangeRef(namedRange: string): string | null {
    const ref = namedRangeLookup.get(namedRange)
    if (!ref || ref.includes(':')) return null
    return ref
  }

  function getNamedTextValue(namedRange: string): string | null {
    const ref = getNamedRangeRef(namedRange)
    if (!ref) return null

    const cell = getCellByReference(workbook, ref)
    const value = getCellText(cell ?? undefined)
    return value || null
  }

  function getNamedNumericValue(namedRange: string): number | null {
    const ref = getNamedRangeRef(namedRange)
    if (!ref) return null

    const cell = getCellByReference(workbook, ref)
    return getCellNumber(cell ?? undefined)
  }

  function getSheetCellNumericValue(sheetName: string, cellRef: string): number | null {
    const sheet = workbook.Sheets[sheetName]
    if (!sheet) return null
    return getCellNumber(sheet[cellRef] ?? undefined)
  }

  function getSheetCellText(sheetName: string, cellRef: string): string {
    const sheet = workbook.Sheets[sheetName]
    if (!sheet) return ''
    return getCellText(sheet[cellRef] ?? undefined)
  }

  return {
    versionNumber,
    mainSheetName,
    namedRanges,
    getNamedTextValue,
    getNamedNumericValue,
    getNamedRangeRef,
    getSheetCellNumericValue,
    getSheetCellText,
  }
}

function toSeasonalValues(value: number): SeasonsT {
  return {
    spring: value,
    summer: value,
    autumn: value,
    winter: value,
  }
}

function getNamedNumericValueOrZero(context: AdccWorkbookContext, namedRange: string): number {
  if (!namedRange) return 0
  return context.getNamedNumericValue(namedRange) ?? 0
}

function getNamedTextValue(context: AdccWorkbookContext, namedRange: string): string {
  if (!namedRange) return ''
  return context.getNamedTextValue(namedRange) ?? ''
}

function clampZeroToOne(value: number): number {
  return Math.min(1, Math.max(0, value))
}

function getFirstAvailableNamedNumericValue(
  context: AdccWorkbookContext,
  namedRanges: string[],
): number {
  for (const namedRange of namedRanges) {
    const value = context.getNamedNumericValue(namedRange)
    if (value !== null) return value
  }

  return 0
}

// Each feed component for milking cows, paired intake + DMD + CP named ranges.
// Used to compute a weighted average since FeedGrazedTotal* only covers pasture
// and will be empty on TMR farms with no grazing.
const MILKER_FEED_COMPONENTS = [
  {
    kgDmRange: 'FeedGrazedTotalKgDM',
    dmdRange: 'FeedGrazedTotalDigestabilityPercent',
    cpRange: 'FeedGrazedTotalProteinPercent',
  },
  {
    kgDmRange: 'FeedFedConcUsableAreaKgDM',
    dmdRange: 'FeedFedConcUsableAreaDigestabilityPercent',
    cpRange: 'FeedFedConcUsableAreaProteinPercent',
  },
  {
    kgDmRange: 'FeedFedSilageUsableAreaKgDM',
    dmdRange: 'FeedFedSilageUsableAreaDigestabilityPercent',
    cpRange: 'FeedFedSilageUsableAreaProteinPercent',
  },
  {
    kgDmRange: 'FeedFedHayUsableAreaKgDM',
    dmdRange: 'FeedFedHayUsableAreaDigestabilityPercent',
    cpRange: 'FeedFedHayUsableAreaProteinPercent',
  },
  {
    kgDmRange: 'FeedFedByProductsUsableAreaKgDM',
    dmdRange: 'FeedFedByProductsUsableAreaDigestabilityPercent',
    cpRange: 'FeedFedByProductsUsableAreaProteinPercent',
  },
  {
    kgDmRange: 'FeedFedOtherUsableAreaKgDM',
    dmdRange: 'FeedFedOtherUsableAreaDigestabilityPercent',
    cpRange: 'FeedFedOtherUsableAreaProteinPercent',
  },
] as const

function calculateMilkerWeightedAverage(
  context: AdccWorkbookContext,
  qualityField: 'dmdRange' | 'cpRange',
): number {
  let totalIntake = 0
  let weightedSum = 0

  for (const component of MILKER_FEED_COMPONENTS) {
    const intake = getNamedNumericValueOrZero(context, component.kgDmRange)
    if (intake <= 0) continue
    totalIntake += intake
    const quality = getNamedNumericValueOrZero(context, component[qualityField])
    if (quality > 0) weightedSum += intake * quality
  }

  return totalIntake > 0 ? weightedSum / totalIntake : 0
}

function mapDairyCattleClassInformation(context: AdccWorkbookContext): DairyCattleT {
  const mappedClasses: DairyCattleT = []

  ADCC_DAIRY_CLASS_MAPPINGS.forEach((mapping) => {
    const headcount = getNamedNumericValueOrZero(context, mapping.headcountRange)
    if (headcount <= 0) return

    const dmd =
      mapping.classId === 1
        ? calculateMilkerWeightedAverage(context, 'dmdRange')
        : getNamedNumericValueOrZero(context, mapping.dmdRange)

    const cp =
      mapping.classId === 1
        ? calculateMilkerWeightedAverage(context, 'cpRange')
        : getNamedNumericValueOrZero(context, mapping.crudeProteinRange)

    mappedClasses.push({
      stableId: null,
      classId: mapping.classId,
      headcount: toSeasonalValues(headcount),
      liveweight: toSeasonalValues(
        getNamedNumericValueOrZero(context, mapping.liveweightRange) ||
          (mapping.classId === 4 ? 800 : 0),
      ),
      liveweightGain: toSeasonalValues(
        getNamedNumericValueOrZero(context, mapping.liveweightGainRange ?? ''),
      ),
      dryMatterDigestability: toSeasonalValues(dmd),
      crudeProteinIntake: toSeasonalValues(cp),
    })
  })

  return mappedClasses
}

function mapMilkProduction(context: AdccWorkbookContext): SeasonsT {
  const annualMilkLitres = getNamedNumericValueOrZero(context, 'MilkLitresFarmTotal')
  const averageMilkingCows = getNamedNumericValueOrZero(context, 'CowMilkerNumber')

  if (annualMilkLitres <= 0 || averageMilkingCows <= 0) {
    return toSeasonalValues(0)
  }
  const litresPerDay = parseFloatWithFixed(annualMilkLitres / averageMilkingCows / 365, 1)
  return toSeasonalValues(litresPerDay)
}

function mapDairySales(context: AdccWorkbookContext): DairyProductionT['sales'] {
  const sales: DairyProductionT['sales'] = []

  ADCC_DAIRY_SALES_MAPPINGS.forEach((mapping) => {
    const numberSold = getNamedNumericValueOrZero(context, mapping.numberSoldRange)
    if (numberSold <= 0) return

    sales.push({
      classId: mapping.classId,
      numberSold,
      avgLiveweight: getNamedNumericValueOrZero(context, mapping.avgLiveweightRange),
      stillWeaningAtSale:
        mapping.classId === 7
          ? !getNamedTextValue(context, 'CalvesSoldWhen').includes('post-weaning')
          : undefined,
    })
  })

  return sales
}

type NewStandardsDairyProductionSale = NonNullable<
  NonNullable<UserInputsT['dairyProduction']>['sales']
>[number]

function adccDairySaleClassToNewStandardsMobType(
  sale: DairyProductionT['sales'][number],
): NewStandardsDairyProductionSale['mobType'] {
  switch (sale.classId) {
    case 1:
      return 'milkingCows'
    case 2:
      return 'heifersOver1'
    case 3:
      return 'heifersUnder1Weaned'
    case 4:
      return 'bullsOver1'
    case 5:
      return 'otherUnder1Weaned'
    case 6:
      return 'bullsOver1'
    case 7:
      return sale.stillWeaningAtSale ? 'heifersPreWeaned' : 'otherUnder1Weaned'
  }
}

function mapDairyProductionToNewStandards(
  context: AdccWorkbookContext,
): NonNullable<UserInputsT['dairyProduction']> {
  const dairyProductionInformation = mapDairyProductionInformation(context)

  return {
    milkProducedLitres: getNamedNumericValueOrZero(context, 'MilkLitresFarmTotal'),
    fatPercent: dairyProductionInformation.milkFatPercentage || undefined,
    proteinPercent: dairyProductionInformation.milkProteinPercentage || undefined,
    sales: dairyProductionInformation.sales.map((sale) => ({
      mobType: adccDairySaleClassToNewStandardsMobType(sale),
      numberSold: sale.numberSold,
      averageLiveweightKg: sale.avgLiveweight,
    })),
    metadata: { formVersion: 'v1' },
  }
}

function mapDairyProductionInformation(context: AdccWorkbookContext): DairyProductionT {
  return {
    milkProduction: mapMilkProduction(context),
    sales: mapDairySales(context),
    milkFatPercentage: getNamedNumericValueOrZero(context, 'MilkFatPercentAnnual'),
    milkProteinPercentage: getNamedNumericValueOrZero(context, 'MilkProteinPercentAnnual'),
  }
}

function mapSupplementaryFeedInformation(context: AdccWorkbookContext): SupplementaryFeedT {
  // v6 range names; v5 uses different names
  const grain = getFirstAvailableNamedNumericValue(context, [
    'FeedConcPurchasedTDM',
    'FeedFedConcPurchasedTDM',
  ])

  const hay =
    context.getNamedNumericValue('FeedHayPurchasedTDM') !== null
      ? // V6
        getNamedNumericValueOrZero(context, 'FeedHayPurchasedTDM') +
        getNamedNumericValueOrZero(context, 'FeedSilagePurchasedTDM')
      : // V5
        getNamedNumericValueOrZero(context, 'FeedFedLegumeLucerneHayPurchasedTDM') +
        getNamedNumericValueOrZero(context, 'FeedFedPastureCerealHayTDM')

  const cottonSeed = 0

  return {
    grain: grain * 1.11,
    hay: hay * 1.14,
    cottonSeed,
  }
}

function estimateUreaTonnes(
  totalAreaHa: number,
  nRateKgPerHa: number,
  ureaPercent: number,
): number {
  if (totalAreaHa <= 0 || nRateKgPerHa <= 0 || ureaPercent <= 0) return 0

  const totalNitrogenKg = totalAreaHa * nRateKgPerHa
  const nitrogenFromUreaKg = totalNitrogenKg * (ureaPercent / 100)

  return nitrogenFromUreaKg / 1000 / UREA_N_FRACTION
}

function estimateNFertRate(totalAreaHa: number, nRateKgPerHa: number, ureaPercent: number): number {
  if (totalAreaHa <= 0 || nRateKgPerHa <= 0 || ureaPercent <= 0) return 0

  const totalNitrogenKg = totalAreaHa * nRateKgPerHa
  const nitrogenFromNonUreaKg = totalNitrogenKg * ((100 - ureaPercent) / 100)

  return nitrogenFromNonUreaKg / totalAreaHa
}

function mapDairyProductionSystemInputsInformation(
  context: AdccWorkbookContext,
  _primaryProductionSystemType?: PropertyDairyProductionSystemT,
  options?: AdccImportOptions,
): DairyProductionSystemRecordT {
  const usingTotalTonnes = (context.getNamedTextValue('FertiliserRateUnits') ?? '')
    .toLowerCase()
    .includes('tonnes')

  let pastureArea = getFirstAvailableNamedNumericValue(context, [
    'FertiliserPasturesNArea',
    'FertiliserPasturesPKSLimeArea',
  ])
  let cropArea = getFirstAvailableNamedNumericValue(context, [
    'FertiliserCropsNArea',
    'FertiliserCropsPKSLimeArea',
  ])

  if (options?.cropAreaHa && options.cropAreaHa > 0) {
    cropArea = options.cropAreaHa
  }

  if (options?.pastureAreaHa && options.pastureAreaHa > 0) {
    pastureArea = options.pastureAreaHa
  }

  const rawPastureNRate = getNamedNumericValueOrZero(context, 'FertiliserPasturesNRate')
  const rawCropNRate = getNamedNumericValueOrZero(context, 'FertiliserCropsNRate')

  // When in tonnes-per-annum mode, rates are total tonnes/year — convert to kg/ha using area.
  // If area is missing (user didn't fill in the modal prompt) the rate falls back to 0 rather
  // than passing raw tonnes as kg/ha, which would be a unit mismatch.
  const pastureNRate =
    usingTotalTonnes && pastureArea > 0
      ? parseFloatWithFixed((rawPastureNRate * 1000) / pastureArea, 1)
      : usingTotalTonnes
        ? 0
        : rawPastureNRate

  const cropNRate =
    usingTotalTonnes && cropArea > 0
      ? parseFloatWithFixed((rawCropNRate * 1000) / cropArea, 1)
      : usingTotalTonnes
        ? 0
        : rawCropNRate

  const ureaPercent = getNamedNumericValueOrZero(context, 'FertiliserUreaPercent')

  const pastureLimeRate = getNamedNumericValueOrZero(context, 'FertiliserPasturesLimeRate')
  const cropLimeRate = getNamedNumericValueOrZero(context, 'FertiliserCropsLimeRate')

  const pasturePRate = getNamedNumericValueOrZero(context, 'FertiliserPasturesPRate')
  const cropPRate = getNamedNumericValueOrZero(context, 'FertiliserCropsPRate')

  const dryland: DairyProductionSystemRecordT['dryland'] = {
    areaImprovedPasture: pastureArea,
    nFertiliserPasture: estimateNFertRate(pastureArea, pastureNRate, ureaPercent),
    ureaFertiliserPasture: estimateUreaTonnes(pastureArea, pastureNRate, ureaPercent),

    // no dryland crops
    areaCropped: 0,
    nFertiliserCrops: 0,
    ureaFertiliserCrops: 0,
  }

  const irrigated: DairyProductionSystemRecordT['irrigated'] = {
    areaCropped: cropArea,
    nFertiliserCrops: estimateNFertRate(cropArea, cropNRate, ureaPercent),
    ureaFertiliserCrops: estimateUreaTonnes(cropArea, cropNRate, ureaPercent),

    // no irrigated pasture
    areaImprovedPasture: 0,
    nFertiliserPasture: 0,
    ureaFertiliserPasture: 0,
  }

  return {
    limestone: usingTotalTonnes
      ? pastureLimeRate + cropLimeRate
      : (pastureArea * pastureLimeRate + cropArea * cropLimeRate) / 1000,
    // Need equivalent tonnes of single super, not of P as an element.
    // So need to divide Tonnes of P by 9% to get to total tonnes
    singleSuperphosphate:
      (usingTotalTonnes
        ? pasturePRate + cropPRate
        : (pastureArea * pasturePRate + cropArea * cropPRate) / 1000) / 0.09,

    irrigated,
    dryland,

    // hard coded
    limestoneFraction: 1,
    herbicide: 0,
    herbicidePesticide: 0,
    otherNfertilisers: [],
  }
}

function mapConsumablesInformation(
  context: AdccWorkbookContext,
): NonNullable<ReportInputsPartialT['consumablesInformation']> {
  // v6 splits diesel into farm + contractors; v5 has a single DieselUse range
  const diesel =
    context.getNamedNumericValue('DieselUseFarm') !== null
      ? getNamedNumericValueOrZero(context, 'DieselUseFarm') +
        getNamedNumericValueOrZero(context, 'DieselUseContractors')
      : getNamedNumericValueOrZero(context, 'DieselUse')

  // v5 has no petrol ranges — will naturally be 0
  const petrol =
    getNamedNumericValueOrZero(context, 'PetrolUseFarm') +
    getNamedNumericValueOrZero(context, 'PetrolUseContractors')

  const electricityUsage = getNamedNumericValueOrZero(context, 'ElectricityPurchased')
  const renewablesPercent = getNamedNumericValueOrZero(
    context,
    'ElectricityPurchasedRenewablePercent',
  )

  const renewablesFromPercent = renewablesPercent > 0 ? renewablesPercent / 100 : 0
  const electricitySource = context.getNamedTextValue('ElectricitySourceId')
  const sourceBasedRenewables = electricitySource?.toLowerCase() === 'renewable' ? 1 : 0

  return {
    diesel,
    petrol,
    electricityUsage,
    percentRenewables: Math.max(renewablesFromPercent, sourceBasedRenewables),
  }
}

function findManureDataRows(
  context: AdccWorkbookContext,
): { milkersRow: string; otherRow: string } | null {
  // There are two 'Anaerobic lagoon' header rows in the sheet:
  //   1. The input/summary table (~row 90) — populated when using default state factors
  //   2. The computed output table (~row 121) — populated when using user-defined factors
  // We find the first occurrence whose milkers row has actual numeric data in column C
  // (the lagoon %), which handles both methods without needing to check the method selector.
  const { mainSheetName } = context
  for (let row = 80; row <= 140; row++) {
    if (context.getSheetCellText(mainSheetName, `C${row}`).includes('Anaerobic lagoon')) {
      const milkersRow = row + 1
      if (context.getSheetCellNumericValue(mainSheetName, `C${milkersRow}`) !== null) {
        return { milkersRow: String(milkersRow), otherRow: String(milkersRow + 1) }
      }
    }
  }
  return null
}

function mapDairyManureManagementInformation(context: AdccWorkbookContext): DairyManureManagementT {
  // Read the ADCC's final computed manure split percentages directly from the main sheet.
  // These cells reflect whichever estimation method the user chose (default state factors or
  // user-defined), so we don't need to branch on ManureWasteMethodId.
  const { mainSheetName } = context
  const rows = findManureDataRows(context)
  const milkersRow = rows?.milkersRow ?? '91'
  const otherRow = rows?.otherRow ?? '92'

  const milkingPasture = clampZeroToOne(
    (context.getSheetCellNumericValue(mainSheetName, `G${milkersRow}`) ?? 0) / 100,
  )
  const milkingLagoon = clampZeroToOne(
    (context.getSheetCellNumericValue(mainSheetName, `C${milkersRow}`) ?? 0) / 100,
  )
  const milkingSumpDispersal = clampZeroToOne(
    (context.getSheetCellNumericValue(mainSheetName, `D${milkersRow}`) ?? 0) / 100,
  )
  const milkingDrainToPaddock = clampZeroToOne(
    (context.getSheetCellNumericValue(mainSheetName, `E${milkersRow}`) ?? 0) / 100,
  )
  const milkingSolidStorage = clampZeroToOne(
    (context.getSheetCellNumericValue(mainSheetName, `F${milkersRow}`) ?? 0) / 100,
  )

  const otherPasture = clampZeroToOne(
    (context.getSheetCellNumericValue(mainSheetName, `G${otherRow}`) ?? 100) / 100,
  )
  const otherLagoon = clampZeroToOne(
    (context.getSheetCellNumericValue(mainSheetName, `C${otherRow}`) ?? 100) / 100,
  )
  const otherSumpDispersal = clampZeroToOne(
    (context.getSheetCellNumericValue(mainSheetName, `D${otherRow}`) ?? 100) / 100,
  )
  const otherDrainToPaddock = clampZeroToOne(
    (context.getSheetCellNumericValue(mainSheetName, `E${otherRow}`) ?? 100) / 100,
  )
  const otherSolidStorage = clampZeroToOne(
    (context.getSheetCellNumericValue(mainSheetName, `F${otherRow}`) ?? 100) / 100,
  )

  return {
    milkingPasture,
    milkingLagoon,
    milkingSumpDispersal,
    milkingDrainToPaddock,
    milkingSolidStorage,
    otherPasture,
    otherLagoon,
    otherSumpDispersal,
    otherDrainToPaddock,
    otherSolidStorage,
  }
}

// Maps ADCC PlantationRegionId text values to Ruminati region IDs.
// Region codes are taken from the Trees sheet lookup table (column C, rows 33–50).
const ADCC_REGION_NAME_TO_RUMINATI_ID: Record<string, number> = {
  'South West WA': 1,
  'Northern coastal NSW': 10,
  'Southern coastal NSW': 11,
  'Central Inland & Riverina NSW': 15,
  'North East TAS': 17,
  'Central North & Midlands TAS': 19,
  'Southern & Derwent Valley TAS': 20,
  'North West TAS': 22,
  'South East SA': 23,
  'Murray River SA': 24,
  'Mallee region VIC': 28,
  'Northern region VIC': 30,
  'North East region VIC': 31,
  'East Gippsland': 32,
  'South & West Gippsland': 33,
  'South West VIC': 35,
  'Northern QLD': 36,
  'Southern QLD': 40,
}

function mapTreeInformation(context: AdccWorkbookContext): TreesT {
  const method = context.getNamedTextValue('PlantationDataMethodId')

  if (!method || method.toLowerCase().includes('no estimate')) {
    return []
  }

  const area = getNamedNumericValueOrZero(context, 'PlantationArea')
  if (area <= 0) return []

  const age = getNamedNumericValueOrZero(context, 'PlantationAge')

  if (method.toLowerCase().includes('based on data')) {
    const regionName = context.getNamedTextValue('PlantationRegionId')
    const speciesName = context.getNamedTextValue('PlantationTypeId')

    const regionId = regionName ? ADCC_REGION_NAME_TO_RUMINATI_ID[regionName] : undefined
    const speciesId =
      regionId !== undefined && speciesName
        ? REGIONAL_VEGETATION_TYPES[regionId]?.find((s) => s.name === speciesName)?.value
        : undefined

    return [{ species: speciesId!, soilType: 1, age, area }]
  }

  // "Carbon sequestered using other tools" – area and age only, no region/species available
  return []
}

export type AdccDairyBreedBreakdownRow = {
  breed: DairyCattleSnapshotEntryT['breed'] | undefined
  percentage: number | undefined
}

type CompleteAdccDairyBreedBreakdownRow = {
  breed: DairyCattleSnapshotEntryT['breed']
  percentage: number
}

export interface AdccImportOptions {
  cropAreaHa?: number
  pastureAreaHa?: number
  reportPeriodStartYear?: number
  reportPeriodStartMonth?: number
  dairyBreedBreakdown?: Array<AdccDairyBreedBreakdownRow>
}

export interface AdccImportRequirements {
  needsCropArea: boolean
  needsPastureArea: boolean
  hasDairyCattleHeadcounts: boolean
}

export function detectAdccImportRequirements(workbook: WorkBook): AdccImportRequirements {
  const context = buildAdccWorkbookContext(workbook)
  const usingTotalTonnes = (context.getNamedTextValue('FertiliserRateUnits') ?? '')
    .toLowerCase()
    .includes('tonnes')

  const hasDairyCattleHeadcounts = ADCC_DAIRY_CLASS_MAPPINGS.some(
    (mapping) => getNamedNumericValueOrZero(context, mapping.headcountRange) > 0,
  )

  if (!usingTotalTonnes) {
    return { needsCropArea: false, needsPastureArea: false, hasDairyCattleHeadcounts }
  }

  const rawCropNRate = getNamedNumericValueOrZero(context, 'FertiliserCropsNRate')
  const cropArea = getFirstAvailableNamedNumericValue(context, [
    'FertiliserCropsNArea',
    'FertiliserCropsPKSLimeArea',
  ])

  const rawPastureNRate = getNamedNumericValueOrZero(context, 'FertiliserPasturesNRate')
  const pastureArea = getFirstAvailableNamedNumericValue(context, [
    'FertiliserPasturesNArea',
    'FertiliserPasturesPKSLimeArea',
  ])

  return {
    needsCropArea: cropArea <= 0 && rawCropNRate > 0,
    needsPastureArea: pastureArea <= 0 && rawPastureNRate > 0,
    hasDairyCattleHeadcounts,
  }
}

export async function openDragAndDroppedFile(data: ArrayBuffer): Promise<WorkBook | undefined> {
  try {
    const { read } = await import('xlsx')

    const workbook: WorkBook = read(data)
    return workbook
  } catch (err) {
    console.log(err)
    return undefined
  }
}

function getAdccImportSnapshotDate(options?: AdccImportOptions): string {
  const year = options?.reportPeriodStartYear ?? new Date().getFullYear()
  const month = String(options?.reportPeriodStartMonth ?? 1).padStart(2, '0')
  return `${year}-${month}-01`
}

function scaleHeadcount(value: number, percentage: number): number | undefined {
  if (value <= 0 || percentage <= 0) return undefined
  return value * (percentage / 100)
}

function positiveValue(value: number): number | undefined {
  return value > 0 ? value : undefined
}

function mapDairyCattleToNewStandards(
  context: AdccWorkbookContext,
  options?: AdccImportOptions,
): NonNullable<UserInputsT['dairyCattle']> {
  const snapshotDate = getAdccImportSnapshotDate(options)
  const breedBreakdown = (options?.dairyBreedBreakdown ?? []).filter(
    (row): row is CompleteAdccDairyBreedBreakdownRow =>
      Boolean(row.breed && row.percentage !== undefined && row.percentage > 0),
  )

  const snapshots: NonNullable<UserInputsT['dairyCattle']>['snapshots'] = breedBreakdown.map(
    (row) => ({
      date: snapshotDate,
      breed: row.breed,
      headcounts: {
        milkingCows: scaleHeadcount(
          getNamedNumericValueOrZero(context, 'CowMilkerNumber'),
          row.percentage,
        ),
        heifersOver1: scaleHeadcount(
          getNamedNumericValueOrZero(context, 'Rising2HeiferNumber'),
          row.percentage,
        ),
        heifersUnder1Weaned: scaleHeadcount(
          getNamedNumericValueOrZero(context, 'Rising1HeiferNumber'),
          row.percentage,
        ),
      },
      headcountsNonReplacement: {
        bullsOver1: scaleHeadcount(
          getNamedNumericValueOrZero(context, 'BullNumber'),
          row.percentage,
        ),
        otherUnder1Weaned: scaleHeadcount(
          getNamedNumericValueOrZero(context, 'OtherLivestockNumber'),
          row.percentage,
        ),
        otherOver1: scaleHeadcount(
          getNamedNumericValueOrZero(context, 'OtherLivestockNotImportedNumber'),
          row.percentage,
        ),
      },
      liveweights: {
        milkingCows: positiveValue(getNamedNumericValueOrZero(context, 'CowLiveweightKg')),
        heifersOver1: positiveValue(
          getNamedNumericValueOrZero(context, 'Rising2HeiferLiveweightKg'),
        ),
        heifersUnder1Weaned: positiveValue(
          getNamedNumericValueOrZero(context, 'Rising1HeiferLiveweightKg'),
        ),
      },
      liveweightsNonReplacement: {
        bullsOver1: positiveValue(getNamedNumericValueOrZero(context, 'BullLiveweightKg') || 800),
        otherUnder1Weaned: positiveValue(
          getNamedNumericValueOrZero(context, 'OtherLivestockLiveweightKg'),
        ),
        otherOver1: positiveValue(
          getNamedNumericValueOrZero(context, 'OtherLivestockNotImportedLiveweightKg'),
        ),
      },
    }),
  )

  return {
    snapshots,
    metadata: { formVersion: 'v1' },
  }
}

function mapConsumablesToNewStandards(
  context: AdccWorkbookContext,
): NonNullable<UserInputsT['consumables']> {
  const consumables = mapConsumablesInformation(context)
  return {
    electricity: { electricityKwhUsed: consumables.electricityUsage },
    fuel: {
      petrol: { quantityLitres: consumables.petrol, fuelType: 'gasoline' },
      diesel: { quantityLitres: consumables.diesel, fuelType: 'diesel' },
      additional: [],
    },
    metadata: { formVersion: 'v1' },
  }
}

function mapFeedsToNewStandards(
  context: AdccWorkbookContext,
): NonNullable<UserInputsT['feedsAndSupplements']> {
  const feed = mapSupplementaryFeedInformation(context)
  const purchasedFeed: NonNullable<UserInputsT['feedsAndSupplements']>['purchasedFeed'] = []
  if (feed.grain > 0) purchasedFeed.push({ quantityTonnes: feed.grain, feedType: 'wheatGrain' })
  if (feed.hay > 0) purchasedFeed.push({ quantityTonnes: feed.hay, feedType: 'pastureHay' })
  if (feed.cottonSeed > 0) {
    purchasedFeed.push({ quantityTonnes: feed.cottonSeed, feedType: 'cottonSeed' })
  }

  return {
    mineralSupplements: [],
    purchasedFeed,
    metadata: { formVersion: 'v1' },
  }
}

function mapDairyManureToNewStandards(
  context: AdccWorkbookContext,
): NonNullable<UserInputsT['dairyManureManagement']> {
  const manure = mapDairyManureManagementInformation(context)
  const pasturePercentageRaw = manure.milkingPasture * 100
  const collectedPercentagesRaw = {
    anaerobicLagoonPercentage: manure.milkingLagoon * 100,
    sumpDispersalSystemPercentage: manure.milkingSumpDispersal * 100,
    drainsToPaddockPercentage: manure.milkingDrainToPaddock * 100,
    solidStoragePercentage: manure.milkingSolidStorage * 100,
  }
  const collectedPercentageRaw = Object.values(collectedPercentagesRaw).reduce(
    (sum, value) => sum + value,
    0,
  )
  const totalPercentageRaw = pasturePercentageRaw + collectedPercentageRaw
  const pasturePercentage =
    totalPercentageRaw > 0 ? (pasturePercentageRaw / totalPercentageRaw) * 100 : 100
  const milkingShedPercentage =
    totalPercentageRaw > 0 ? (collectedPercentageRaw / totalPercentageRaw) * 100 : 0

  function collectedShare(value: number): number {
    return collectedPercentageRaw > 0 ? (value / collectedPercentageRaw) * 100 : 0
  }

  return {
    feedingSystem: 'custom',
    timeSplit: {
      pasturePercentage,
      milkingShedPercentage,
      feedpadPercentage: 0,
    },
    milkingShed: {
      anaerobicLagoonPercentage: collectedShare(collectedPercentagesRaw.anaerobicLagoonPercentage),
      sumpDispersalSystemPercentage: collectedShare(
        collectedPercentagesRaw.sumpDispersalSystemPercentage,
      ),
      drainsToPaddockPercentage: collectedShare(collectedPercentagesRaw.drainsToPaddockPercentage),
      solidStoragePercentage: collectedShare(collectedPercentagesRaw.solidStoragePercentage),
    },
    feedpad: {
      anaerobicLagoonPercentage: 0,
      sumpDispersalSystemPercentage: 0,
      drainsToPaddockPercentage: 0,
      solidStoragePercentage: 0,
    },
    metadata: { formVersion: 'v1' },
  }
}

function mapPastureToNewStandards(
  context: AdccWorkbookContext,
  primaryProductionSystemType?: PropertyDairyProductionSystemT,
  options?: AdccImportOptions,
): NonNullable<UserInputsT['pasture']> {
  const productionSystemInputs = mapDairyProductionSystemInputsInformation(
    context,
    primaryProductionSystemType,
    options,
  )
  const fertilisers: NonNullable<UserInputsT['pasture']>['fertilisers'] = []
  const dryland = productionSystemInputs.dryland
  const irrigated = productionSystemInputs.irrigated

  function addUrea(params: { massTonnes: number; proportionDryland: number }) {
    if (params.massTonnes <= 0) return
    fertilisers.push({
      fertiliserName: 'Urea',
      inorganicFertiliserMassKg: params.massTonnes * 1000,
      volatilisationType: 'urea-based',
      components: { n: 46, p: 0, k: 0, s: 0 },
      proportionOfNitrogenAsUrea: 100,
      proportionDryland: params.proportionDryland,
    })
  }

  addUrea({
    massTonnes: dryland.ureaFertiliserPasture,
    proportionDryland: 100,
  })
  addUrea({
    massTonnes: irrigated.ureaFertiliserCrops,
    proportionDryland: 0,
  })

  return {
    pastureRenewalAreas: {},
    lime: {
      limeTonnesUsed: productionSystemInputs.limestone,
      limestoneFraction: productionSystemInputs.limestoneFraction * 100,
    },
    fertilisers,
    agrichemicals: [],
    metadata: { formVersion: 'v1' },
  }
}

export function extractLegacyGafDataFromWorkbook(
  workbook: WorkBook,
  primaryProductionSystemType?: PropertyDairyProductionSystemT,
  options?: AdccImportOptions,
): ReportInputsPartialT {
  const context = buildAdccWorkbookContext(workbook)

  const dairyCattleClassInformation = mapDairyCattleClassInformation(context)
  const dairyProductionInformation = mapDairyProductionInformation(context)
  const consumablesInformation = mapConsumablesInformation(context)
  const supplementaryFeedInformation = mapSupplementaryFeedInformation(context)
  const dairyProductionSystemInputsInformation = mapDairyProductionSystemInputsInformation(
    context,
    primaryProductionSystemType,
    options,
  )
  const dairyManureManagementInformation = mapDairyManureManagementInformation(context)
  const treesInformation = mapTreeInformation(context)

  return {
    dairyCattleClassInformation,
    dairyProductionInformation,
    consumablesInformation,
    supplementaryFeedInformation,
    dairyProductionSystemInputsInformation,
    dairyManureManagementInformation,
    treesInformation,

    mineralSupplementsInformation: [],
    dairyTransportInformation: [],
  }
}

export function extractNewStandardsDataFromWorkbook(
  workbook: WorkBook,
  primaryProductionSystemType?: PropertyDairyProductionSystemT,
  options?: AdccImportOptions,
): Partial<UserInputsT> {
  const context = buildAdccWorkbookContext(workbook)

  // TODO: This is the first-pass ADCC -> new-standards mapping. The straightforward fields are
  // mapped below, but several domain decisions still need to be confirmed before we treat this as
  // complete:
  // - Dairy breed is not supplied by ADCC, so the import modal prompts for a breed split before
  //   writing dairy cattle snapshots.
  // - ADCC calf population data are not yet mapped into new-standards cattle snapshots.
  // - Manure is mapped into the milking shed split only; confirm whether/how to infer feedpad use.
  // - Pasture/fertiliser currently imports lime and urea only; add non-urea N, P/single-super and
  //   better production-system allocation once the preferred representation is agreed.
  // - ADCC tree/plantation data has no obvious new-standards dairy form destination yet.
  return {
    dairyCattle: mapDairyCattleToNewStandards(context, options),
    dairyProduction: mapDairyProductionToNewStandards(context),
    consumables: mapConsumablesToNewStandards(context),
    feedsAndSupplements: mapFeedsToNewStandards(context),
    dairyManureManagement: mapDairyManureToNewStandards(context),
    pasture: mapPastureToNewStandards(context, primaryProductionSystemType, options),
  }
}
